import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, F, btn } from "@/components/CaseBits";

export const InvitePanel = ({ kase, caseId, onDone }) => {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [made, setMade] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRows = useCallback(() => {
    api.get(`/cases/${caseId}/invites`).then(({ data }) => setRows(data)).catch(() => {});
  }, [caseId]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const mySide = kase.my_side;
  const options = mySide === "claimant"
    ? [["claimant_client", "Invite the claimant (your client)"],
       ["respondent_advocate", "Invite the respondent's counsel"]]
    : [["respondent_client", "Invite the respondent (your client)"]];
  const [role, setRole] = useState(options[0][0]);

  const gate = (r) => {
    if (r === "respondent_advocate") {
      if (!kase.clients?.length) return "Invite or tag the claimant first";
      if (!kase.claimant_paid) return "The claimant's half must be paid first";
      if (kase.respondent_advocate) return "Respondent's counsel already on record";
    }
    return "";
  };
  const blocked = gate(role);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/invites`, { role, email, phone: phone || null });
      setMade(data);
      setEmail("");
      setPhone("");
      toast.success(data.phone ? "Invite sent by e-mail, SMS and WhatsApp" : "Invite link ready — share it by WhatsApp or copy it");
      loadRows();
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made.url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select and copy the link manually");
    }
  };

  return (
    <Section title="Invite links" testId="invite-panel">
      <div className="px-6 py-5 space-y-5">
        <p className="text-xs text-muted-foreground">
          Each link is tied to one email, works once and lapses in seven days. Add a mobile number to also send
          it by SMS and WhatsApp. The recipient joins the matter and is taken straight to their share of the case fee.
        </p>
        <form onSubmit={generate} className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_auto] gap-4 items-end"
          data-testid="invite-generate-form">
          <div className="space-y-2">
            <Label className="label-caps">Who are you inviting</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-none h-10" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <F label="Their email" type="email" required value={email} onChange={setEmail}
            testId="invite-email-input" placeholder="name@example.com" />
          <F label="Their mobile (optional)" type="tel" value={phone} onChange={setPhone}
            testId="invite-phone-input" placeholder="+91 98765 43210" />
          <Button type="submit" disabled={busy || !!blocked} className={btn} data-testid="invite-generate-button">
            {busy ? "Creating…" : "Create link"}
          </Button>
        </form>
        {blocked && <p className="text-xs text-amber-800 dark:text-amber-400" data-testid="invite-blocked">{blocked}</p>}

        {made && (
          <div className="border border-blue-900/40 bg-blue-50 dark:bg-blue-950/30 p-5" data-testid="invite-created">
            <p className="label-caps">Link for {made.email}{made.phone ? ` · ${made.phone}` : ""}</p>
            <p className="font-mono text-xs break-all mt-2" data-testid="invite-link">{made.url}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button onClick={copy} data-testid="invite-copy-button"
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white uppercase tracking-widest text-xs h-10">
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy link
              </Button>
              <a href={made.whatsapp_url} target="_blank" rel="noreferrer" data-testid="invite-whatsapp-button"
                className="inline-flex items-center h-10 px-4 bg-green-700 hover:bg-green-600 text-white uppercase tracking-widest text-xs transition-colors duration-200">
                <Share2 className="h-3.5 w-3.5 mr-2" /> {made.phone ? "Open in WhatsApp" : "Share on WhatsApp"}
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3" data-testid="invite-sent-summary">
              {made.phone
                ? `Sent to ${made.email} by e-mail and to ${made.phone} by SMS and WhatsApp${/MOCKED/.test(made.sent?.sms || "") ? " (SMS/WhatsApp delivery activates once Twilio is configured)" : ""}.`
                : `The same link has already gone to ${made.email} by email.`}
            </p>
          </div>
        )}
      </div>
      {rows.length > 0 && rows.map((r) => (
        <div key={r.id} className="px-6 py-4 border-t border-border flex flex-wrap items-center justify-between gap-3"
          data-testid={`invite-row-${r.id}`}>
          <div>
            <p className="text-sm">{r.email}{r.phone ? <span className="text-muted-foreground font-mono text-xs"> · {r.phone}</span> : null}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {r.role_label} · issued by {r.invited_by_name} · lapses {fmtDate(r.expires_at)}
            </p>
          </div>
          <Badge className={r.used ? "bg-green-50 text-green-900" : "bg-amber-50 text-amber-900"}
            testId={`invite-status-${r.id}`}>{r.used ? "accepted" : "pending"}</Badge>
        </div>
      ))}
    </Section>
  );
};
